import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Collapse,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { calculatePasswordStrength } from '../../utils/validation';
import { ALLOWED_PASSWORD_SYMBOLS, PASSWORD_MIN_LENGTH, createPasswordSymbolPattern } from '../../../../shared/constants/password';

interface PasswordStrengthMeterProps {
  password: string;
  showFeedback?: boolean;
  size?: 'small' | 'medium';
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  showFeedback = true,
  size = 'medium',
}) => {
  const strength = calculatePasswordStrength(password);

  // Color mapping for strength levels
  const getStrengthColor = (label: string) => {
    switch (label) {
      case 'weak':
        return 'error';
      case 'fair':
        return 'warning';
      case 'good':
        return 'info';
      case 'strong':
        return 'success';
      default:
        return 'error';
    }
  };

  // Japanese labels for strength levels
  const getStrengthLabel = (label: string) => {
    switch (label) {
      case 'weak':
        return '弱い';
      case 'fair':
        return '普通';
      case 'good':
        return '良い';
      case 'strong':
        return '強い';
      default:
        return '弱い';
    }
  };

  const progressValue = (strength.score / 4) * 100;
  const strengthColor = getStrengthColor(strength.label);
  const strengthLabel = getStrengthLabel(strength.label);

  // Password requirements checklist
  const requirements = [
    {
      label: `${PASSWORD_MIN_LENGTH}文字以上`,
      met: password.length >= PASSWORD_MIN_LENGTH,
    },
    {
      label: '小文字を含む',
      met: /[a-z]/.test(password),
    },
    {
      label: '大文字を含む',
      met: /[A-Z]/.test(password),
    },
    {
      label: '数字を含む',
      met: /\d/.test(password),
    },
    {
      label: '記号を含む',
      met: new RegExp(createPasswordSymbolPattern()).test(password),
      description: `使用可能な記号: ${ALLOWED_PASSWORD_SYMBOLS}`,
    },
  ];

  if (!password) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {/* Progress bar and label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            color={strengthColor as any}
            sx={{
              height: size === 'small' ? 4 : 6,
              borderRadius: 2,
              bgcolor: 'grey.200',
            }}
          />
        </Box>
        <Chip
          label={strengthLabel}
          color={strengthColor as any}
          size={size}
          variant="outlined"
        />
      </Box>

      {/* Feedback and requirements */}
      {showFeedback && (
        <Box>
          {/* Requirements checklist */}
          <List dense sx={{ py: 0 }}>
            {requirements.map((req, index) => (
              <Box key={index}>
                <ListItem sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {req.met ? (
                    <CheckCircleIcon 
                      color="success" 
                      sx={{ fontSize: size === 'small' ? 16 : 20 }} 
                    />
                  ) : (
                    <RadioButtonUncheckedIcon 
                      color="disabled" 
                      sx={{ fontSize: size === 'small' ? 16 : 20 }} 
                    />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={req.label}
                  primaryTypographyProps={{
                    variant: size === 'small' ? 'caption' : 'body2',
                    color: req.met ? 'success.main' : 'text.secondary',
                  }}
                />
              </ListItem>
                {/* 記号の説明表示 */}
                {(req as any).description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ 
                      display: 'block', 
                      ml: 4, 
                      mt: -0.5, 
                      mb: 0.5,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    {(req as any).description}
                  </Typography>
                )}
              </Box>
            ))}
          </List>


        </Box>
      )}
    </Box>
  );
};

export default PasswordStrengthMeter;